import { Pencil, Reply, Trash2 } from 'lucide-react';
import { useGroupChannelContext } from '@sendbird/uikit-react/GroupChannel/context';
import {
  MessageMenu,
  type MessageMenuProps,
} from '@sendbird/uikit-react/ui/MessageMenu';
import { Role } from '@sendbird/chat';
import { useTranslate } from 'ra-core';
import { ForwardMenuItem } from './ForwardMenuItem';
import { PinUnpinMenuItem } from './PinUnpinMenuItem';
import { useHasPermission } from '../../../../auth/useHasPermission';
import { useMemo } from 'react';

export const MessageCustomMenu: React.FC<MessageMenuProps> = (menuProps) => {
  const translate = useTranslate();
  const { currentChannel } = useGroupChannelContext();
  const canEditMessage =
    useHasPermission('CHAT_MESSAGE_EDIT_MY_MESSAGES') && menuProps.isByMe;

  const hasPermissionToDeleteMessages = useHasPermission(
    'CHAT_MESSAGE_DELETE_MY_MESSAGES'
  );

  const canDeleteMessage = useMemo(() => {
    if (!hasPermissionToDeleteMessages) {
      return false;
    } else if (menuProps.isByMe) {
      return true;
    } else if (currentChannel.myRole === Role.OPERATOR) {
      return true;
    } else {
      return false;
    }
  }, [hasPermissionToDeleteMessages, menuProps.isByMe, currentChannel]);

  return (
    <MessageMenu
      {...menuProps}
      renderMenuItems={(props) => {
        const { message, channel } = menuProps;
        const { DeleteMenuItem, ReplyMenuItem, EditMenuItem } = props.items;
        const isPinned = channel?.pinnedMessageIds.includes(message.messageId);

        return (
          <>
            <ReplyMenuItem>
              <MenuItemBoxContainer>
                <Reply />
                <span>{translate('chat.reply')}</span>
              </MenuItemBoxContainer>
            </ReplyMenuItem>

            <ForwardMenuItem message={message} />

            {!currentChannel.isSuper &&
              currentChannel.myRole === Role.OPERATOR && (
                <PinUnpinMenuItem
                  channel={currentChannel}
                  message={message}
                  isPinned={isPinned}
                />
              )}

            {canEditMessage && (
              <EditMenuItem>
                <MenuItemBoxContainer>
                  <Pencil />
                  <span>{translate('chat.edit')}</span>
                </MenuItemBoxContainer>
              </EditMenuItem>
            )}

            {canDeleteMessage && (
              <DeleteMenuItem>
                <MenuItemBoxContainer>
                  <Trash2 />
                  <span>{translate('chat.delete')}</span>
                </MenuItemBoxContainer>
              </DeleteMenuItem>
            )}
          </>
        );
      }}
    />
  );
};

export const MenuItemBoxContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => <span className="flex items-center gap-2">{children}</span>;
